(() => {
  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const header = qs('[data-site-header]');
  const drawer = qs('[data-cart-layer]');
  const menu = qs('[data-mobile-menu]');
  const rootPath = window.Shopify?.routes?.root || '/';

  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  })[character]);

  const formatMoney = (amount) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(amount);
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency
    }).format(amount / 100);
  };

  const syncPageLock = () => {
    document.body.classList.toggle('is-locked', Boolean(drawer?.classList.contains('open') || menu?.classList.contains('open')));
  };

  const setDrawer = (open) => {
    if (!drawer) return;
    drawer.classList.toggle('open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    syncPageLock();
    if (open) qs('[data-cart-close]', drawer)?.focus();
  };

  const setMenu = (open) => {
    if (!menu) return;
    menu.classList.toggle('open', open);
    menu.setAttribute('aria-hidden', String(!open));
    syncPageLock();
  };

  const cartItemMarkup = (item) => {
    const itemUrl = escapeHtml(item.url || '#');
    const image = item.image
      ? '<a class="cart-drawer-item-image" href="' + itemUrl + '"><img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy"></a>'
      : '<a class="cart-drawer-item-image" href="' + itemUrl + '" aria-label="' + escapeHtml(item.product_title) + '"></a>';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? '<span>' + escapeHtml(item.variant_title) + '</span>'
      : '';
    const decreaseLabel = escapeHtml(drawer?.dataset.quantityDecrease || '');
    const increaseLabel = escapeHtml(drawer?.dataset.quantityIncrease || '');
    const removeLabel = escapeHtml(drawer?.dataset.removeLabel || '');

    return '<article class="cart-drawer-item" data-line-key="' + escapeHtml(item.key) + '">' +
      image +
      '<div><small>' + escapeHtml(item.vendor || '') + '</small>' +
      '<a class="cart-drawer-item-title" href="' + itemUrl + '">' + escapeHtml(item.product_title) + '</a>' +
      variant +
      '<p>' + formatMoney(item.final_line_price) + '</p>' +
      '<div class="cart-drawer-item-actions">' +
      '<div class="cart-drawer-quantity">' +
      '<button type="button" data-cart-change data-line-key="' + escapeHtml(item.key) + '" data-quantity="' + Math.max(0, item.quantity - 1) + '" aria-label="' + decreaseLabel + '">−</button>' +
      '<span>' + item.quantity + '</span>' +
      '<button type="button" data-cart-change data-line-key="' + escapeHtml(item.key) + '" data-quantity="' + (item.quantity + 1) + '" aria-label="' + increaseLabel + '">＋</button>' +
      '</div>' +
      '<button class="cart-drawer-remove" type="button" data-cart-change data-line-key="' + escapeHtml(item.key) + '" data-quantity="0">' + removeLabel + '</button>' +
      '</div></div></article>';
  };

  const renderCart = async () => {
    const response = await fetch(rootPath + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(drawer?.dataset.updateError || '');
    const cart = await response.json();

    qsa('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
    });

    if (!drawer) return cart;
    const heading = qs('[data-cart-heading]', drawer);
    const content = qs('[data-cart-content]', drawer);
    const footer = qs('[data-cart-footer]', drawer);
    const subtotal = qs('[data-cart-subtotal]', drawer);

    if (heading) {
      heading.textContent = cart.item_count
        ? drawer.dataset.filledHeading
        : drawer.dataset.emptyHeading;
    }

    if (content) {
      if (cart.item_count) {
        content.innerHTML = cart.items.map(cartItemMarkup).join('');
      } else {
        content.innerHTML = '<div class="cart-drawer-empty"><p>' +
          escapeHtml(drawer.dataset.emptyCopy || '') +
          '</p><a class="button primary" href="' + rootPath + 'collections/all">' +
          escapeHtml(drawer.dataset.exploreLabel || '') +
          '</a></div>';
      }
    }

    if (footer) footer.hidden = !cart.item_count;
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    return cart;
  };

  const updateCartLine = async (key, quantity) => {
    const status = qs('[data-cart-status]', drawer);
    if (status) status.textContent = '';
    const response = await fetch(rootPath + 'cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) {
      if (status) status.textContent = drawer?.dataset.updateError || '';
      return;
    }
    await renderCart();
  };

  const swapProductMedia = (section, source, alt, index) => {
    const image = qs('.product-main-media', section);
    if (!image || !source) return;
    image.style.opacity = '0';
    window.setTimeout(() => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = source;
      image.alt = alt || '';
      image.style.opacity = '1';
    }, 120);

    qsa('[data-product-thumbnail]', section).forEach((thumbnail) => {
      const active = thumbnail.dataset.mediaSrc === source || thumbnail.dataset.mediaIndex === String(index);
      thumbnail.classList.toggle('active', active);
      thumbnail.setAttribute('aria-pressed', String(active));
    });

    const counter = qs('[data-media-current]', section);
    if (counter && index) counter.textContent = index;
  };

  document.addEventListener('click', async (event) => {
    const thumbnail = event.target.closest('[data-product-thumbnail]');
    if (thumbnail) {
      const section = thumbnail.closest('[data-product-section]');
      swapProductMedia(section, thumbnail.dataset.mediaSrc, thumbnail.dataset.mediaAlt, thumbnail.dataset.mediaIndex);
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const form = quantityButton.closest('form');
      const input = qs('[name="quantity"]', form);
      if (input) {
        const nextValue = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(quantityButton.dataset.quantityChange));
        input.value = nextValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const cartChange = event.target.closest('[data-cart-change]');
    if (cartChange) {
      event.preventDefault();
      cartChange.disabled = true;
      await updateCartLine(cartChange.dataset.lineKey, Number(cartChange.dataset.quantity));
      return;
    }

    const cartCheckout = event.target.closest('[data-cart-checkout]');
    if (cartCheckout) {
      event.preventDefault();
      window.location.assign(cartCheckout.href);
      return;
    }

    if (event.target.closest('[data-cart-open]')) {
      event.preventDefault();
      setDrawer(true);
      try {
        await renderCart();
      } catch (error) {
        const status = qs('[data-cart-status]', drawer);
        if (status) status.textContent = drawer?.dataset.updateError || '';
      }
      return;
    }

    if (event.target.closest('[data-cart-close]') || event.target === drawer) {
      setDrawer(false);
      return;
    }

    if (event.target.closest('[data-menu-open]')) {
      setMenu(true);
      return;
    }

    if (event.target.closest('[data-menu-close]')) setMenu(false);

    const ritualButton = event.target.closest('[data-ritual-stage]');
    if (ritualButton) {
      const scene = ritualButton.closest('[data-ritual-scene]');
      setRitualStage(scene, Number(ritualButton.dataset.ritualStage));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    setDrawer(false);
    setMenu(false);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-ajax-cart-form]');
    if (!form) return;
    event.preventDefault();

    const section = form.closest('[data-product-section]');
    const buttons = qsa('[data-add-button]', section);
    const status = qs('[data-product-status]', section);
    buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    if (status) status.textContent = '';

    try {
      const response = await fetch(rootPath + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || section.dataset.addError);

      await renderCart();
      buttons.forEach((button) => {
        const label = qs('[data-add-label]', button);
        if (label) label.textContent = section.dataset.addedLabel;
      });
      if (status) status.textContent = section.dataset.addedLabel;
      setDrawer(true);

      window.setTimeout(() => {
        buttons.forEach((button) => {
          const label = qs('[data-add-label]', button);
          if (label) label.textContent = section.dataset.addLabel;
        });
      }, 1800);
    } catch (error) {
      if (status) status.textContent = error.message || section.dataset.addError;
    } finally {
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    const section = select.closest('[data-product-section]');
    const option = select.selectedOptions[0];
    const available = option.dataset.available === 'true';

    qsa('[data-product-price]', section).forEach((node) => {
      node.textContent = option.dataset.price;
    });
    qsa('[data-add-button]', section).forEach((button) => {
      button.disabled = !available;
      const label = qs('[data-add-label]', button);
      if (label) label.textContent = available ? section.dataset.addLabel : section.dataset.soldOutLabel;
    });

    if (option.dataset.mediaSrc) {
      const thumbnail = qsa('[data-product-thumbnail]', section).find((item) => item.dataset.mediaSrc === option.dataset.mediaSrc);
      swapProductMedia(section, option.dataset.mediaSrc, option.dataset.mediaAlt, thumbnail?.dataset.mediaIndex);
    }
  });

  const setRitualStage = (scene, index) => {
    if (!scene) return;
    qsa('[data-ritual-image]', scene).forEach((image, imageIndex) => image.classList.toggle('active', imageIndex === index));
    qsa('[data-ritual-stage]', scene).forEach((button, buttonIndex) => button.classList.toggle('active', buttonIndex <= index));
    qsa('[data-ritual-copy]', scene).forEach((copy, copyIndex) => {
      copy.hidden = copyIndex !== index;
    });
  };

  const updateScrollScenes = () => {
    if (header) header.classList.toggle('solid', window.scrollY > 50);
    qsa('[data-scroll-scene]').forEach((scene) => {
      const rect = scene.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      const progress = Math.max(0, Math.min(1, -rect.top / travel));
      scene.style.setProperty('--progress', progress.toFixed(4));
      if (scene.matches('[data-ritual-scene]')) {
        const stages = qsa('[data-ritual-stage]', scene);
        const index = Math.min(stages.length - 1, Math.floor(progress * stages.length));
        setRitualStage(scene, index);
      }
    });
  };

  let frame = 0;
  const requestUpdate = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(updateScrollScenes);
  };

  updateScrollScenes();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);
  document.addEventListener('shopify:section:load', requestUpdate);
})();
